import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rulesAPI } from '@/utils/api';
import useAppStore from '@/store/appStore';

export function useRules(filters = {}) {
  return useQuery({
    queryKey: ['rules', filters],
    queryFn: async () => {
      const response = await rulesAPI.getAll(filters);
      return response.data;
    },
  });
}

export function useRule(id) {
  return useQuery({
    queryKey: ['rules', id],
    queryFn: async () => {
      const response = await rulesAPI.getById(id);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateRule() {
  const queryClient = useQueryClient();
  const { addNotification } = useAppStore();

  return useMutation({
    mutationFn: (data) => rulesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rules'] });
      addNotification({
        type: 'success',
        title: 'Rule Created',
        message: 'Rule created successfully',
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Failed to Create Rule',
        message: error.message,
      });
    },
  });
}

export function useUpdateRule() {
  const queryClient = useQueryClient();
  const { addNotification } = useAppStore();

  return useMutation({
    mutationFn: ({ id, data }) => rulesAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rules'] });
      addNotification({
        type: 'success',
        title: 'Rule Updated',
        message: 'Rule updated successfully',
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Failed to Update Rule',
        message: error.message,
      });
    },
  });
}

export function useDeleteRule() {
  const queryClient = useQueryClient();
  const { addNotification } = useAppStore();

  return useMutation({
    mutationFn: (id) => rulesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rules'] });
      addNotification({
        type: 'success',
        title: 'Rule Deleted',
        message: 'Rule deleted successfully',
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Failed to Delete Rule',
        message: error.message,
      });
    },
  });
}

export function useReorderRules() {
  const queryClient = useQueryClient();
  const { addNotification } = useAppStore();

  return useMutation({
    mutationFn: (orderedIds) => rulesAPI.reorder(orderedIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rules'] });
      addNotification({
        type: 'success',
        title: 'Rules Reordered',
        message: 'Rule priorities updated',
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Failed to Reorder Rules',
        message: error.message,
      });
    },
  });
}

export function useTestRule() {
  const { addNotification } = useAppStore();

  return useMutation({
    mutationFn: ({ id, sampleEmails }) => rulesAPI.test(id, sampleEmails),
    onSuccess: (response) => {
      addNotification({
        type: 'info',
        title: 'Rule Test Complete',
        message: `Matched ${response.data?.matches || 0} emails`,
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Rule Test Failed',
        message: error.message,
      });
    },
  });
}
